package dev.danko.roblox_craft_ai.uikit.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import org.jetbrains.compose.web.css.CSSNumericValue
import org.jetbrains.compose.web.css.CSSUnitLength
import org.jetbrains.compose.web.css.CSSUnitLengthOrPercentage
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

@Immutable
data class AppColorPalette(
    val background: Color,
    val surface: Color,
    val surfaceElevated: Color,
    val border: Color,
    /** Borders of controls (buttons, inputs), a step stronger than card borders. */
    val borderStrong: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    /** Fill of primary buttons; white text on it passes WCAG AA. */
    val accent: Color,
    val accentGlow: Color,
    /** Accent for text and icons on dark surfaces (links, active tabs). */
    val accentText: Color,
    val robloxRed: Color,
    val statusPending: Color,
    val statusProcessing: Color,
    val statusReady: Color,
    val statusFailed: Color,
)

@Immutable
data class AppRadii(
    val sm: CSSNumericValue<out CSSUnitLengthOrPercentage> = 6.px,
    val md: CSSNumericValue<out CSSUnitLengthOrPercentage> = 10.px,
    val lg: CSSNumericValue<out CSSUnitLengthOrPercentage> = 16.px,
    val xl: CSSNumericValue<out CSSUnitLengthOrPercentage> = 20.px,
    val full: CSSNumericValue<out CSSUnitLengthOrPercentage> = 9999.px,
)

@Immutable
data class AppShadows(
    val card: String = "0 1px 2px rgba(0,0,0,0.3)",
    val cardHover: String = "0 8px 24px rgba(0,0,0,0.35)",
    val accentGlow: String = "0 0 0 3px rgba(124,92,255,0.35)",
)

@Immutable
data class AppSpacing(
    val xs: CSSNumericValue<out CSSUnitLength> = 0.25.cssRem,
    val sm: CSSNumericValue<out CSSUnitLength> = 0.5.cssRem,
    val md: CSSNumericValue<out CSSUnitLength> = 1.cssRem,
    val lg: CSSNumericValue<out CSSUnitLength> = 1.5.cssRem,
    val xl: CSSNumericValue<out CSSUnitLength> = 2.cssRem,
    val xxl: CSSNumericValue<out CSSUnitLength> = 3.cssRem,
)

@Immutable
data class AppThemeTokens(
    val colors: AppColorPalette,
    val radii: AppRadii = AppRadii(),
    val shadows: AppShadows = AppShadows(),
    val spacing: AppSpacing = AppSpacing(),
)

// Style A "Dark studio" (mockups approved 2026-10-03). Changing the brand means changing these tokens.
private val DarkPalette = AppColorPalette(
    background = Color.rgb(0x0D0E12),
    surface = Color.rgb(0x15171D),
    surfaceElevated = Color.rgb(0x1C1F27),
    border = Color.rgb(0x262A34),
    borderStrong = Color.rgb(0x343948),
    textPrimary = Color.rgb(0xECEEF3),
    textSecondary = Color.rgb(0x9AA1B2),
    accent = Color.rgb(0x7C5CFF),
    accentGlow = Color.rgb(0x9B7BFF),
    accentText = Color.rgb(0xB9A3FF),
    robloxRed = Color.rgb(0xE2231A),
    statusPending = Color.rgb(0xF5B544),
    statusProcessing = Color.rgb(0x9B7BFF),
    statusReady = Color.rgb(0x34D399),
    statusFailed = Color.rgb(0xF87171),
)

private val LightPalette = AppColorPalette(
    background = Color.rgb(0xF6F7F9),
    surface = Color.rgb(0xFFFFFF),
    surfaceElevated = Color.rgb(0xF1F2F6),
    border = Color.rgb(0xE2E4EA),
    borderStrong = Color.rgb(0xCDD1DA),
    textPrimary = Color.rgb(0x14161C),
    textSecondary = Color.rgb(0x575D6E),
    accent = Color.rgb(0x6A47F5),
    accentGlow = Color.rgb(0x8B6CFF),
    accentText = Color.rgb(0x5B39E0),
    robloxRed = Color.rgb(0xE2231A),
    statusPending = Color.rgb(0xB7791F),
    statusProcessing = Color.rgb(0x6A47F5),
    statusReady = Color.rgb(0x0E9F6E),
    statusFailed = Color.rgb(0xD64545),
)

/** Interface and code typefaces, served by Bunny Fonts (see the site's head). */
const val UI_FONT_STACK = "Manrope, system-ui, -apple-system, 'Segoe UI', sans-serif"
const val CODE_FONT_STACK = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"

val LocalAppTheme = staticCompositionLocalOf<AppThemeTokens> {
    error("AppTheme not provided")
}

fun ColorMode.toAppPalette(): AppColorPalette = when (this) {
    ColorMode.DARK -> DarkPalette
    ColorMode.LIGHT -> LightPalette
}

@Composable
fun ProvideAppTheme(content: @Composable () -> Unit) {
    val colorMode = ColorMode.current
    val tokens = AppThemeTokens(colors = colorMode.toAppPalette())
    CompositionLocalProvider(LocalAppTheme provides tokens) {
        content()
    }
}

val BackgroundColorVar by StyleVariable<Color>()
val SurfaceColorVar by StyleVariable<Color>()
val AccentColorVar by StyleVariable<Color>()

@InitSilk
fun initAppTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.dark.background = DarkPalette.background
    ctx.theme.palettes.dark.color = DarkPalette.textPrimary
    ctx.theme.palettes.light.background = LightPalette.background
    ctx.theme.palettes.light.color = LightPalette.textPrimary
}

fun Modifier.appSurface(theme: AppThemeTokens): Modifier = this
    .backgroundColor(theme.colors.surface)
    .borderRadius(theme.radii.lg)

fun Modifier.accentGlow(theme: AppThemeTokens): Modifier = this
    .boxShadow(color = theme.colors.accentGlow, blurRadius = 2.cssRem)
